<template>
	<div ref="overlayRef" class="overlay" :data-for="dataFor" :data-placement="placement === 'below' ? 'below' : undefined">
		<slot></slot>
	</div>
</template>
<script lang="ts">
import { defineComponent, onBeforeUnmount, onMounted, PropType, ref } from 'vue'

export default defineComponent({
	name: 'Overlay',
	props: {
		/**
		 * 錨點元素的 id，面板以它為中心對齊
		 */
		dataFor: {
			type: String,
			required: true,
		},
		/**
		 * 開在錨點的上方或下方，錨點在畫面上方時要用 below
		 */
		placement: {
			type: String as PropType<'above' | 'below'>,
			default: 'above',
		},
	},
	emits: ['close'],
	setup(props, { emit }) {
		// 面板元素參考
		const overlayRef = ref<HTMLElement | null>(null)
		// 延後掛上點擊監聽的計時器
		let listenTimer: ReturnType<typeof setTimeout> | undefined

		/**
		 * 以錨點為中心定位面板，左右夾在畫面內各留 5px，與錨點距離 10px
		 * @return {void}
		 */
		const position = (): void => {
			// 面板不存在就不做事
			const overlay = overlayRef.value
			if (!overlay) {
				return
			}

			// 找不到錨點就不做事
			const anchor = document.getElementById(props.dataFor)
			if (!anchor) {
				return
			}

			// 取得錨點與面板的尺寸
			const anchorRect = anchor.getBoundingClientRect()
			const overlayRect = overlay.getBoundingClientRect()
			// 置中於錨點，並夾在畫面左右邊界內
			const left = anchorRect.left + anchorRect.width / 2 - overlayRect.width / 2
			const maxLeft = window.innerWidth - overlayRect.width - 5

			overlay.style.left = `${Math.min(Math.max(left, 5), maxLeft)}px`
			// 往下開只設 top，預設往上開只設 bottom
			if (props.placement === 'below') {
				overlay.style.top = `${anchorRect.bottom + 10}px`
				overlay.style.removeProperty('bottom')
			} else {
				overlay.style.removeProperty('top')
				overlay.style.bottom = `${window.innerHeight - anchorRect.top + 10}px`
			}
			// 移除原有的 transform
			overlay.style.transform = 'none'
		}

		/**
		 * 這一次按下的起點是否在面板或錨點內
		 */
		let isPressInside = false

		/**
		 * 記錄按下的起點，拖曳放開在外面時才不會被當成點外面
		 * @param event {MouseEvent}
		 * @return {void}
		 */
		const handleDocumentPress = (event: MouseEvent): void => {
			const target = event.target as Node
			// 起點在面板或錨點內就記起來
			isPressInside = !!(overlayRef.value?.contains(target) || document.getElementById(props.dataFor)?.contains(target))
		}

		/**
		 * 點在面板與錨點以外的地方就發出 close
		 * @param event {MouseEvent}
		 * @return {void}
		 */
		const handleDocumentClick = (event: MouseEvent): void => {
			// 取出起點並重置，下一次點擊重新判斷
			const pressedInside = isPressInside
			isPressInside = false
			// 起點在面板或錨點內，放開在外面也不關
			if (pressedInside) {
				return
			}

			const target = event.target as Node
			// 點在面板內不關
			if (overlayRef.value?.contains(target)) {
				return
			}
			// 點在錨點上不關，由錨點自己切換開關
			if (document.getElementById(props.dataFor)?.contains(target)) {
				return
			}

			emit('close')
		}

		// 掛載後定位，並監聽視窗大小與點擊
		onMounted(() => {
			// 延遲一拍確保 DOM 已完全渲染，也避開開啟它的那一下點擊
			listenTimer = setTimeout(() => {
				position()
				document.addEventListener('pointerdown', handleDocumentPress)
				document.addEventListener('mousedown', handleDocumentPress)
				document.addEventListener('click', handleDocumentClick)
			}, 0)
			window.addEventListener('resize', position)
		})

		// 卸載時移除所有監聽
		onBeforeUnmount(() => {
			clearTimeout(listenTimer)
			window.removeEventListener('resize', position)
			document.removeEventListener('pointerdown', handleDocumentPress)
			document.removeEventListener('mousedown', handleDocumentPress)
			document.removeEventListener('click', handleDocumentClick)
		})

		return {
			overlayRef,
		}
	},
})
</script>
<style lang="scss">
// 三個彈出面板共用的外殼
.overlay {
	position: fixed;
	z-index: 1000;
	min-width: 300px;
	background: var(--ui-white);
	border: 1px solid #e0e0e0;
	border-radius: var(--radius-popover);
	box-shadow: var(--shadow-popover);

	// 語速面板寬度由六顆按鈕決定
	&.overlay-speed {
		min-width: 0;
	}
}
</style>
